import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { auth } from "../../services/firebase";
import { endpoint } from "../../services/shared";
import type { LogFilters } from "./LeadDispatchLogs";

interface Props {
  open: boolean;
  onClose: () => void;
  currentFilters: LogFilters;
}

const MAX_RANGE_DAYS = 31;

function daysBetween(fromIso: string, toIso: string): number {
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

export default function ExportCsvDialog({ open, onClose, currentFilters }: Props) {
  const [from, setFrom] = useState(currentFilters.from);
  const [to, setTo] = useState(currentFilters.to);
  const [includePayload, setIncludePayload] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Sync when dialog reopens
  React.useEffect(() => {
    if (open) {
      setFrom(currentFilters.from);
      setTo(currentFilters.to);
      setIncludePayload(false);
      setError(null);
      setSuccessMsg(null);
    }
  }, [open, currentFilters.from, currentFilters.to]);

  const handleExport = async () => {
    setError(null);
    setSuccessMsg(null);

    const days = daysBetween(from, to);
    if (days < 0) {
      setError("La data di fine deve essere successiva alla data di inizio");
      return;
    }
    if (days > MAX_RANGE_DAYS) {
      setError("Il range massimo è 31 giorni");
      return;
    }

    setRunning(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setError("Sessione scaduta — effettua di nuovo il login");
        setRunning(false);
        return;
      }
      const params = new URLSearchParams({
        from,
        to,
        include_payload: includePayload ? "1" : "0",
      });
      if (currentFilters.ruleId) params.set("ruleId", currentFilters.ruleId);
      if (currentFilters.status !== "all")
        params.set("status", currentFilters.status);
      if (currentFilters.apiKeyId)
        params.set("apiKeyId", currentFilters.apiKeyId);

      const url = `${endpoint}/v1/lead-dispatch/logs/export.csv?${params.toString()}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 422) {
          setError("Il range massimo per l'export è 31 giorni");
        } else if (res.status === 403) {
          setError("Accesso non autorizzato");
        } else {
          setError(`Errore export (${res.status})`);
        }
        setRunning(false);
        return;
      }
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `lead-dispatch-logs-${from}-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      setSuccessMsg("Export completato — il file è stato scaricato.");
    } catch (e) {
      console.error(e);
      setError("Errore inatteso durante l'export");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !running && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Export log CSV</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          I filtri attivi (rule, status, API key) verranno applicati anche
          all'export. Range massimo: 31 giorni.
        </Typography>

        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Da"
              type="date"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="A"
              type="date"
              size="small"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={includePayload}
                onChange={(e) => setIncludePayload(e.target.checked)}
              />
            }
            label="Includi payload snapshots"
          />

          {includePayload && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600}>
                Attenzione GDPR
              </Typography>
              <Typography variant="caption">
                I <code>payload_snapshot</code> possono contenere dati
                personali anche redatti. Includili solo in presenza di
                richiesta DPO documentata. L'export è loggato lato server.
              </Typography>
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {successMsg && <Alert severity="success">{successMsg}</Alert>}
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Filtri attivi:{" "}
            {[
              currentFilters.ruleId && `rule=${currentFilters.ruleId}`,
              currentFilters.status !== "all" && `status=${currentFilters.status}`,
              currentFilters.apiKeyId && `apiKey=${currentFilters.apiKeyId}`,
            ]
              .filter(Boolean)
              .join(" · ") || "nessuno"}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          Chiudi
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={running}
          startIcon={running ? <CircularProgress size={14} /> : undefined}
        >
          {running ? "Export in corso…" : "Avvia export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
